import { Controller } from "@hotwired/stimulus"

// Makes the thread pane answer to the LAST row that was clicked, and say so while it is working.
//
// Turbo supersedes a frame navigation by aborting the request in flight, which is the right answer
// while the request is still on the wire and no answer at all once it is not. A long thread spends
// most of its cost after the response arrives — parsing a megabyte of HTML, rendering it, and
// connecting a Stimulus controller per card — and a request that has already been received cannot
// be aborted. So the sequence that produced "I clicked another row and nothing happened" is:
//
//   1. click a long thread; its response lands and starts rendering
//   2. click a short one; Turbo has nothing left to abort, and requests it
//   3. the short one renders, then the long one finishes and replaces it
//
// The pane ends on the thread the reader was LEAVING, with the list highlighting the one they
// chose. Clicking again appears to do nothing, because the second click supersedes correctly and
// then loses the same race.
//
// The fix is to judge every response against what the reader last asked for, at the point Turbo
// offers it — before the parse, so a superseded response costs nothing rather than costing the
// most expensive part of itself. A response for any other thread is dropped, and one that has
// already rendered leaves the cover up: the newer request is still coming, and the alternative is
// showing the wrong thread for a moment on the way.
//
// Only the pane's own frame navigations. Every form inside the thread — the composer, the header's
// pickers, a reaction — fetches with itself as the target, not the frame, which is what #ownNavigation
// filters on. Turbo's hover prefetch does the same, so warming a row can never be mistaken for
// choosing it.
//
// Leaving for another list is the other way to change your mind. The list is a separate frame, so
// Turbo neither waits on nor cancels the pane's request when the sidebar navigates it, and a long
// thread would go on downloading, parsing and rendering — freezing the page the reader has just
// moved to — for a thread they are no longer looking at. So the pane owns an abort of its own, and
// pulls it when the list frame starts a navigation while a thread is still on its way.
const LOADING = "is-loading"

export default class extends Controller {
  static targets = ["empty"]
  static values = { frame: String, list: String }

  disconnect() {
    this.abortController?.abort()
    this.clear()
  }

  // The moment of intent. Turbo fires this after the click and before the request goes out, so it
  // is also the earliest honest place to raise the cover.
  //
  // Turbo's own signal is kept alongside ours rather than replaced, so its row → row supersession
  // still aborts exactly as it did.
  requested(event) {
    if (!this.ownNavigation(event)) return

    const { fetchOptions } = event.detail
    this.abortController = new AbortController()
    fetchOptions.signal = AbortSignal.any([ fetchOptions.signal, this.abortController.signal ])

    delete this.abandoned
    this.pendingPath = event.detail.url.pathname
    this.element.classList.add(LOADING)
  }

  // Listens on the document: the list frame is not inside the pane. Only the frame's own
  // navigations — a sidebar link — count; a form in the list (search, a row's archive button)
  // fetches with itself as the target, and leaves the reader where they are.
  //
  // The pane empties rather than dropping the cover over what it showed before: that is the thread
  // the reader had already clicked away from, in a list they have now left too.
  leaving(event) {
    if (event.target?.id !== this.listValue) return
    if (!this.element.classList.contains(LOADING)) return

    this.abortController?.abort()
    this.abandoned = true
    this.clear()
    this.showEmpty()
  }

  // A response whose body had already arrived by the time the reader left cannot be aborted; its
  // render is swallowed here instead, so the thread never lands on top of the list they moved to.
  beforeRender(event) {
    if (!this.ownNavigation(event) || !this.abandoned) return

    event.detail.render = () => {}
  }

  // Cancelling here means Turbo never reads the body, so a superseded long thread is dropped for
  // the price of its headers.
  received(event) {
    if (!this.ownNavigation(event)) return
    if (this.wanted(event.detail.fetchResponse)) return

    event.preventDefault()
  }

  // A render this pane did not ask for still happened — it was accepted before the reader changed
  // their mind — so the cover stays until the one they did ask for arrives.
  rendered(event) {
    if (!this.ownNavigation(event)) return
    if (!this.wanted(event.detail.fetchResponse)) return

    this.clear()
  }

  // A request that failed, or a response with no frame in it, ends the wait whatever it was for:
  // nothing else is coming, and a cover with nothing behind it is worse than the error underneath.
  // Turbo swallows its own aborts rather than reporting them here, so superseding never lands here.
  failed() {
    this.clear()
  }

  clear() {
    delete this.pendingPath
    this.element.classList.remove(LOADING)
  }

  // src goes too, so nothing that reloads the frame later brings the abandoned thread back.
  showEmpty() {
    const frame = this.element.querySelector(`#${this.frameValue}`)
    if (!frame || !this.hasEmptyTarget) return

    frame.removeAttribute("src")
    frame.replaceChildren(this.emptyTarget.content.cloneNode(true))
  }

  // A response that did not come back from where it was sent is not this pane's to judge — an
  // expired session redirected to the login page is still the answer to the last click, and
  // dropping it would leave the cover up over nothing.
  wanted(fetchResponse) {
    if (fetchResponse.redirected) return true

    return new URL(fetchResponse.response.url).pathname === this.pendingPath
  }

  ownNavigation(event) {
    return event.target?.id === this.frameValue
  }
}
